<template>
  <div
    ref="dpplTreeselect"
    class="d-flex flex-column align-start justify-start dppl-treeselect"
  >
    <div class="d-flex align-end justify-space-between actions pr-4">
      <div
        ref="searchInput"
        class="d-flex flex-column align-start justify-start actions"
      >
        <TreeselectHeader
          :search-query="searchQuery"
          :current-options="currentOptions"
          :all-are-selected="allAreSelected"
          :label="label"
          :show-select-all="showSelectAll"
          @select-all="selectAll"
        />
        <TreeselectSearchInline
          v-if="searchAppearance === 'inline'"
          v-model="searchQuery"
          :selected="modelValue"
          :is-select="!flat"
          :placeholder="placeholder"
          :label-key="labelKey"
          :id-key="idKey"
          @delete-key-down="handleDelete"
          @remove-option="removeOption"
          @focus="opened = true"
        >
          <template #chip-prepend="{ option: chipOption }">
            <slot
              name="chip-prepend"
              :option="chipOption"
            />
          </template>
        </TreeselectSearchInline>
        <TreeselectSearch
          v-else
          v-model="searchQuery"
          :selected="modelValue"
          :is-select="!flat"
          :placeholder="placeholder"
          :opened="opened"
          :label-key="labelKey"
          :id-key="idKey"
          @update:selected-filters="$emit('update:modelValue', $event)"
          @delete-key-down="handleDelete"
          @remove-option="removeOption"
          @focus="opened = true"
          @toggle-dropdown="opened = !opened"
        />
      </div>
      <v-btn
        v-if="showInlineApply"
        color="brandPrimary"
        rounded="xl"
        class="ml-4 mb-1"
        elevation="0"
        @click="$emit('apply-filters')"
      >
        Apply
      </v-btn>
    </div>
    <div
      v-if="flat && !hideOptions"
      ref="treeselectDropdown"
      class="actions pt-1"
    >
      <TreeselectLoadingText
        v-if="loading || (isAsyncMode && !currentOptions.length)"
        :is-loading="loading"
      />
      <DpplTreeview
        v-else
        :options="currentOptions"
        :model-value="modelValue"
        :max-height="maxHeight"
        @update:model-value="$emit('update:modelValue', $event)"
      >
        <template #option-label="{ option: slotOption }">
          <slot
            name="option-label"
            :option="slotOption"
          />
        </template>
      </DpplTreeview>
    </div>
  </div>
  <div
    v-if="!flat && opened && !hideOptions"
    ref="treeselectDropdown"
    class="action"
    :class="{ opened: !flat && opened }"
  >
    <div
      class="tree-view-dropdown pt-2"
      :class="directionClass"
      :style="{ minWidth: `${width}px` }"
    >
      <TreeselectLoadingText
        v-if="loading || (isAsyncMode && !currentOptions.length)"
        :is-loading="loading"
      />
      <DpplTreeview
        v-else
        :options="currentOptions"
        :model-value="modelValue"
        :max-height="maxHeight"
        :id-key="idKey"
        @update:model-value="$emit('update:modelValue', $event)"
      >
        <template #option-label="{ option: slotOption }">
          <slot
            name="option-label"
            :option="slotOption"
          />
        </template>
      </DpplTreeview>
    </div>
  </div>
</template>

<script setup>
import {
  ref, watch, computed,
} from 'vue';
import DpplTreeview from '@/components/datapeople/DpplTreeview.vue';
import { flattenNestedOptions, createSearchIndex, lookupOptions } from '@/stores/filters/helpers';
import { debounce } from 'lodash-es';
import TreeselectSearch from '@/components/shared/Treeselect/TreeselectSearch.vue';
import TreeselectSearchInline from '@/components/shared/Treeselect/TreeselectSearchInline.vue';
import TreeselectHeader from '@/components/shared/Treeselect/TreeselectHeader.vue';
import TreeselectLoadingText from '@/components/shared/Treeselect/TreeselectLoadingText.vue';
import { getAsyncSearchResults } from '@/api/asyncSearch';
import { useElementSize, onClickOutside } from '@vueuse/core';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    default: () => [],
  },
  maxHeight: {
    type: Number,
    default: 300,
  },
  placeholder: {
    type: String,
    default: 'Select',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  async: {
    type: Boolean,
    default: false,
  },
  asyncUrl: {
    type: Object,
    default: () => ({}),
  },
  // Custom search function. When set, this runs instead of asyncUrl.
  asyncSearch: {
    type: Function,
    default: null,
  },
  label: {
    type: String,
    default: '',
  },
  showSelectAll: {
    type: Boolean,
    default: false,
  },
  showInlineApply: {
    type: Boolean,
    default: false,
  },
  flat: {
    type: Boolean,
    default: false,
  },
  attachDropdown: {
    type: String,
    default: 'body',
  },
  responseValueKey: {
    type: String,
    default: '',
  },
  labelKey: {
    type: String,
    default: 'label',
  },
  searchKeys: {
    type: Array,
    default: () => [],
  },
  idKey: {
    type: String,
    default: 'id',
  },
  hideOptions: {
    type: Boolean,
    default: false,
  },
  searchAppearance: {
    type: String,
    default: 'default',
  },
});

const emits = defineEmits(['input', 'open', 'searchChange', 'update:modelValue', 'apply-filters', 'add-snackbar']);

// State
const dpplTreeselect = ref(null);
const searchInput = ref(null);
const treeselectDropdown = ref(null);
const searchQuery = ref('');
const currentOptions = ref([]);
const loading = ref(false);
const opened = ref(false);

const isAsyncMode = computed(() => (
  props.async || typeof props.asyncSearch === 'function'
));

const { width } = useElementSize(dpplTreeselect);

const flattenedOptions = computed(() => flattenNestedOptions(
  props.options,
  props.searchKeys.length ? props.searchKeys : [props.labelKey],
  props.idKey,
));
const searchIndex = computed(() => createSearchIndex(
  flattenedOptions.value,
  props.searchKeys.length ? props.searchKeys : [props.labelKey],
));
const allAreSelected = computed(() => searchQuery.value.length > 2 && currentOptions.value
  .every((option) => props.modelValue
    .some(({ [props.idKey]: id }) => id === option[props.idKey])));

// If the dropdown is going to overflow off the screen we want to open it on top
const directionClass = computed(() => {
  if (opened.value && dpplTreeselect.value && treeselectDropdown.value) {
    const { bottom } = dpplTreeselect.value.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const dropdownHeight = treeselectDropdown.value.querySelector('.tree-view-dropdown').clientHeight;

    if (bottom + dropdownHeight > windowHeight) {
      return 'open-on-top';
    }
    return '';
  }
  return '';
});

// Methods
const handleAsyncSearch = async (searchQueryVal) => {
  if (searchQueryVal) {
    try {
      loading.value = true;

      let matchingOptions;

      if (typeof props.asyncSearch === 'function') {
        matchingOptions = await props.asyncSearch(searchQueryVal);
      } else {
        matchingOptions = await getAsyncSearchResults({
          query: searchQueryVal,
          urlObj: props.asyncUrl,
        });
      }

      const newValue = props.responseValueKey
        ? matchingOptions[props.responseValueKey]
        : matchingOptions;

      currentOptions.value = !searchQuery.value ? [] : (newValue || []);
    } catch (error) {
      emits('add-snackbar', { text: 'There was an error loading your search results' });

      throw error;
    } finally {
      loading.value = false;
    }
  } else {
    currentOptions.value = [];
  }
};

const handleSearch = (searchQueryVal, options) => {
  if (searchQueryVal) {
    const matchingOptions = lookupOptions(searchQueryVal, searchIndex.value);

    currentOptions.value = matchingOptions;
  } else {
    currentOptions.value = options;
  }
};

const updateOptions = debounce(async (options, searchQueryVal) => {
  if (isAsyncMode.value) {
    handleAsyncSearch(searchQueryVal);
  } else {
    handleSearch(searchQueryVal, options);
  }
}, 300);

const removeOption = (option) => {
  const newModelValue = props.modelValue
    .filter(({ [props.idKey]: id }) => id !== option[props.idKey]);

  emits('update:modelValue', newModelValue);
};

const selectAll = () => {
  if (allAreSelected.value) {
    emits('update:modelValue', []);
  } else {
    emits('update:modelValue', currentOptions.value);
  }
};

const handleDelete = () => {
  if (!searchQuery.value && props.modelValue.length) {
    emits('update:modelValue', props.modelValue.slice(0, -1));
  }
};

// Watchers
watch(
  [() => props.options, () => searchQuery.value],
  ([options, searchQueryVal]) => {
    updateOptions(options, searchQueryVal);
  },
  { deep: true, immediate: true },
);

onClickOutside(searchInput, () => {
  opened.value = false;
  searchQuery.value = '';
}, { ignore: [treeselectDropdown] });
</script>

<style lang="less" scoped>
@import '@/less/mixins';
.dppl-treeselect {
  overflow-y: auto;
}

.actions {
  width: 100%;
}

:deep(.v-btn) {
  text-transform: none;
  width: 63px;
  height: 32px;
  letter-spacing: normal;

  .v-btn__content {
    font-size: 12px;
    line-height: normal;
    font-weight: 500;
  }
}

.opened {
  height: 0px;
  overflow: visible;
  position: relative;
}

.tree-view-dropdown {
  position: absolute;
  min-width: 238px;
  z-index: 2;
  background: @white;
  border-radius: 6px;
  .shadow();

  &.open-on-top {
    transform: translateY(calc(-100% - 43px));
  }
}
</style>
